import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api.js';

interface TrendPeriod {
  period: string;
  dateRange: string | null;
  sales: number; profit: number; orders: number; units: number;
  organicSales: number; organicOrders: number;
  ppcSales: number; ppcSpend: number; ppcOrders: number; ppcPct: number;
  cogs: number; cm1: number; cm2: number;
  referralFee: number; fbaFee: number; storageFee: number;
  promotions: number; refunds: number; refundUnits: number;
  impressions: number; clicks: number; sessions: number;
  conversion: number; ctr: number; cpc: number;
  acos: number; tacos: number; margin: number; roi: number;
  avgOrderValue: number;
  sqpImpressions: number; sqpYourImpressions: number;
  sqpClicks: number; sqpYourClicks: number;
  sqpCartAdds: number; sqpYourCartAdds: number;
  sqpPurchases: number; sqpYourPurchases: number;
  sqpImpressionShare: number; sqpClickShare: number; sqpPurchaseShare: number;
  sqpTerms: number;
}

const MARKETPLACE_LABELS: Record<string, string> = {
  ATVPDKIKX0DER: 'US', A2EUQ1WTGCTBG2: 'CA', A1AM78C64UM0Y8: 'MX',
  A1F83G8C2ARO7P: 'UK', A1PA6795UKMFR9: 'DE', A13V1IB3VIYZZH: 'FR',
  APJ6JRA9NG5V4: 'IT', A1RKKUPIHCS9HS: 'ES', A1805IZSGTT6HS: 'NL',
  A2NODRKZP88ZB9: 'SE',
};

const fmtUsd = (n: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
const fmtNum = (n: number) => new Intl.NumberFormat('en-US').format(n);
const pct = (n: number) => `${n.toFixed(1)}%`;

function cellColor(val: number, goodAbove: number, badBelow: number, invert = false): string {
  if (invert) {
    if (val <= badBelow) return 'bg-green-50 text-green-800';
    if (val >= goodAbove) return 'bg-red-50 text-red-800';
    return 'bg-yellow-50 text-yellow-800';
  }
  if (val >= goodAbove) return 'bg-green-50 text-green-800';
  if (val <= badBelow) return 'bg-red-50 text-red-800';
  return 'bg-yellow-50 text-yellow-800';
}

// ── Metric row definitions ─────────────────────────────────────────────────

interface MetricDef {
  key: keyof TrendPeriod;
  label: string;
  format: (v: number) => string;
  colorFn?: (v: number) => string;
  defaultVisible?: boolean;
}

const ALL_METRICS: MetricDef[] = [
  { key: 'sales', label: 'Sales', format: fmtUsd, defaultVisible: true },
  { key: 'profit', label: 'Profit', format: fmtUsd, colorFn: (v) => cellColor(v, 1, 0), defaultVisible: true },
  { key: 'orders', label: 'Orders', format: fmtNum, defaultVisible: true },
  { key: 'units', label: 'Units', format: fmtNum, defaultVisible: true },
  { key: 'organicOrders', label: 'Organic Orders', format: fmtNum, defaultVisible: true },
  { key: 'organicSales', label: 'Organic Sales', format: fmtUsd, defaultVisible: true },
  { key: 'ppcSales', label: 'PPC Sales', format: fmtUsd, defaultVisible: true },
  { key: 'ppcSpend', label: 'PPC Spend', format: fmtUsd, defaultVisible: true },
  { key: 'ppcOrders', label: 'PPC Orders', format: fmtNum, defaultVisible: true },
  { key: 'ppcPct', label: 'PPC Share', format: pct, defaultVisible: true },
  { key: 'sessions', label: 'Sessions', format: fmtNum, defaultVisible: true },
  { key: 'conversion', label: 'Conversion', format: pct, colorFn: (v) => cellColor(v, 10, 3), defaultVisible: true },
  { key: 'refundUnits', label: 'Refunds', format: fmtNum, defaultVisible: true },
  { key: 'cogs', label: 'COGS', format: fmtUsd, defaultVisible: true },
  { key: 'referralFee', label: 'Referral Fee', format: fmtUsd, defaultVisible: true },
  { key: 'fbaFee', label: 'FBA Fee', format: fmtUsd, defaultVisible: true },
  { key: 'storageFee', label: 'Storage Fee', format: fmtUsd, defaultVisible: false },
  { key: 'promotions', label: 'Promotions', format: fmtUsd, defaultVisible: false },
  { key: 'impressions', label: 'PPC Impressions', format: fmtNum, defaultVisible: false },
  { key: 'clicks', label: 'PPC Clicks', format: fmtNum, defaultVisible: false },
  { key: 'cpc', label: 'Cost Per Click', format: (v) => `$${v.toFixed(2)}`, defaultVisible: false },
  { key: 'ctr', label: 'CTR', format: pct, defaultVisible: false },
  { key: 'acos', label: 'ACoS', format: pct, colorFn: (v) => cellColor(v, 40, 20, true), defaultVisible: true },
  { key: 'tacos', label: 'TACoS', format: pct, colorFn: (v) => cellColor(v, 15, 8, true), defaultVisible: true },
  { key: 'roi', label: 'ROI', format: pct, colorFn: (v) => cellColor(v, 50, 0), defaultVisible: false },
  { key: 'margin', label: 'Margin', format: pct, colorFn: (v) => cellColor(v, 20, 5), defaultVisible: true },
  { key: 'avgOrderValue', label: 'Avg Order Value', format: fmtUsd, defaultVisible: false },
  // SQP (Brand Analytics search funnel)
  { key: 'sqpTerms', label: 'Search Terms', format: fmtNum, defaultVisible: false },
  { key: 'sqpYourImpressions', label: 'SQP Impressions', format: fmtNum, defaultVisible: false },
  { key: 'sqpImpressionShare', label: 'Impr Share', format: pct, defaultVisible: false },
  { key: 'sqpYourClicks', label: 'SQP Clicks', format: fmtNum, defaultVisible: false },
  { key: 'sqpClickShare', label: 'Click Share', format: pct, defaultVisible: false },
  { key: 'sqpYourCartAdds', label: 'SQP Cart Adds', format: fmtNum, defaultVisible: false },
  { key: 'sqpYourPurchases', label: 'SQP Purchases', format: fmtNum, defaultVisible: false },
  { key: 'sqpPurchaseShare', label: 'Purch Share', format: pct, defaultVisible: false },
];

// ── Column config (localStorage) ───────────────────────────────────────────

const STORAGE_KEY = 'sellvana_trends_columns';

function loadColumnConfig(): { visible: string[]; order: string[] } {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return {
    visible: ALL_METRICS.filter(c => c.defaultVisible).map(c => c.key),
    order: ALL_METRICS.map(c => c.key),
  };
}

function saveColumnConfig(cfg: { visible: string[]; order: string[] }) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(cfg));
}

function ColumnConfig({ config, onChange }: {
  config: { visible: string[]; order: string[] };
  onChange: (cfg: { visible: string[]; order: string[] }) => void;
}) {
  const [open, setOpen] = useState(false);
  const [dragIdx, setDragIdx] = useState<number | null>(null);

  const ordered = config.order.map(k => ALL_METRICS.find(c => c.key === k)!).filter(Boolean);

  const toggle = (key: string) => {
    const vis = config.visible.includes(key)
      ? config.visible.filter(k => k !== key)
      : [...config.visible, key];
    const next = { ...config, visible: vis };
    onChange(next); saveColumnConfig(next);
  };

  const move = (from: number, to: number) => {
    const order = [...config.order];
    const [item] = order.splice(from, 1);
    order.splice(to, 0, item);
    const next = { ...config, order };
    onChange(next); saveColumnConfig(next);
  };

  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)}
        className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 flex items-center gap-1">
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
        </svg>
        Rows
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full z-50 mt-1 w-56 rounded-xl border border-gray-200 bg-white shadow-lg p-2 max-h-80 overflow-y-auto">
            <p className="text-[10px] uppercase text-gray-400 font-semibold px-2 py-1">Drag to reorder</p>
            {ordered.map((col, i) => (
              <div key={col.key}
                draggable
                onDragStart={() => setDragIdx(i)}
                onDragOver={e => e.preventDefault()}
                onDrop={() => { if (dragIdx !== null && dragIdx !== i) move(dragIdx, i); setDragIdx(null); }}
                className={`flex items-center gap-2 rounded-lg px-2 py-1.5 cursor-grab hover:bg-gray-50 ${dragIdx === i ? 'opacity-50' : ''}`}>
                <input type="checkbox" checked={config.visible.includes(col.key)}
                  onChange={() => toggle(col.key)}
                  className="rounded border-gray-300 text-brand-600 h-3.5 w-3.5" />
                <span className="text-xs text-gray-700 flex-1">{col.label}</span>
                <svg className="w-3 h-3 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                </svg>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── Product grouping (same as Orders) ──────────────────────────────────────

interface CatalogProduct { asin: string; sku: string; title: string }
interface ProductGroup { label: string; asins: string[]; products: CatalogProduct[] }

function groupProducts(products: CatalogProduct[]): ProductGroup[] {
  const seen = new Set<string>();
  const filtered = products.filter(p => {
    if (!p.title?.trim() || seen.has(p.asin)) return false;
    seen.add(p.asin);
    return true;
  });
  const groups = new Map<string, CatalogProduct[]>();
  for (const p of filtered) {
    const label = p.title.includes('Walkie') ? 'Walkie Talkies'
      : p.title.includes('CarPlay') || p.title.includes('Android') ? 'CarPlay Adapter'
      : p.title.slice(0, 30);
    if (!groups.has(label)) groups.set(label, []);
    groups.get(label)!.push(p);
  }
  return [...groups.entries()].map(([label, prods]) => ({
    label, asins: [...new Set(prods.map(p => p.asin))], products: prods,
  }));
}

// ── Event tags ─────────────────────────────────────────────────────────────

interface EventTag {
  id: number; name: string; date: string; color: string; notes: string | null;
}

// ── Component ──────────────────────────────────────────────────────────────

export function SalesTrends() {
  const [view, setView] = useState<'weekly' | 'monthly'>('weekly');
  const [days, setDays] = useState(90);
  const [data, setData] = useState<TrendPeriod[]>([]);
  const [loading, setLoading] = useState(true);
  const [showChart, setShowChart] = useState(true);
  const [tags, setTags] = useState<EventTag[]>([]);
  const [showTagForm, setShowTagForm] = useState(false);
  const [newTag, setNewTag] = useState({ name: '', date: '', color: '#3b82f6', notes: '' });

  // Filters
  const [marketplace, setMarketplace] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('');
  const [selectedAsin, setSelectedAsin] = useState('');
  const [productGroups, setProductGroups] = useState<ProductGroup[]>([]);
  const [availableMarkets, setAvailableMarkets] = useState<string[]>([]);

  // Column config
  const [colConfig, setColConfig] = useState(loadColumnConfig);

  const visibleMetrics = colConfig.order
    .filter(k => colConfig.visible.includes(k))
    .map(k => ALL_METRICS.find(c => c.key === k)!)
    .filter(Boolean);

  // Load available marketplaces + product catalog once
  useEffect(() => {
    api.post<{ rows: Array<{ marketplace: string }> }>(
      '/admin/query',
      { sql: "SELECT DISTINCT marketplace FROM amazon_orders WHERE marketplace IS NOT NULL ORDER BY marketplace" },
    ).then(r => setAvailableMarkets(r.rows.map(row => row.marketplace))).catch(() => {});

    api.post<{ rows: Array<{ asin: string; sku: string; title: string }> }>(
      '/admin/query',
      { sql: "SELECT c.asin, c.sku, c.title FROM amazon_catalog c WHERE c.asin IS NOT NULL AND c.title IS NOT NULL AND c.title != '' AND c.sku NOT LIKE 'amzn.gr.%' GROUP BY c.asin ORDER BY c.title" },
    ).then(r => setProductGroups(groupProducts(r.rows))).catch(() => {});
  }, []);

  const activeGroup = productGroups.find(g => g.label === selectedGroup);
  const effectiveAsin = selectedAsin || (activeGroup ? activeGroup.asins.join(',') : '');

  const load = useCallback(() => {
    setLoading(true);
    const end = new Date().toISOString().slice(0, 10);
    const start = new Date(Date.now() - days * 86400000).toISOString().slice(0, 10);
    const params = new URLSearchParams({ view, start, end });
    if (marketplace) params.set('marketplace', marketplace);
    if (effectiveAsin) params.set('asin', effectiveAsin);

    Promise.all([
      api.get<{ periods: TrendPeriod[] }>(`/sales/trends?${params}`),
      api.get<EventTag[]>('/sales/event-tags').catch(() => [] as EventTag[]),
    ]).then(([r, t]) => { setData(r.periods); setTags(t); })
      .finally(() => setLoading(false));
  }, [view, days, marketplace, effectiveAsin]);

  useEffect(() => { load(); }, [load]);

  const addTag = async () => {
    if (!newTag.name || !newTag.date) return;
    const res = await api.post<{ id: number }>('/sales/event-tags', newTag);
    setTags([{ ...newTag, id: res.id, notes: newTag.notes || null }, ...tags]);
    setNewTag({ name: '', date: '', color: '#3b82f6', notes: '' });
    setShowTagForm(false);
  };

  const deleteTag = async (id: number) => {
    await api.delete(`/sales/event-tags/${id}`);
    setTags(tags.filter((t) => t.id !== id));
  };

  // Chart dimensions
  const chartW = Math.max(data.length * 60, 400);
  const chartH = 200;
  const pad = { top: 10, right: 10, bottom: 30, left: 60 };
  const plotW = chartW - pad.left - pad.right;
  const plotH = chartH - pad.top - pad.bottom;

  const maxSales = Math.max(...data.map((d) => d.sales), 1);

  function buildArea(values: number[], baseline: number[]): string {
    if (values.length === 0) return '';
    const xStep = plotW / Math.max(values.length - 1, 1);
    const top = values.map((v, i) => {
      const x = pad.left + i * xStep;
      const y = pad.top + plotH - ((baseline[i] + v) / maxSales) * plotH;
      return `${x},${y}`;
    });
    const bottom = [...baseline].reverse().map((v, i) => {
      const idx = values.length - 1 - i;
      const x = pad.left + idx * xStep;
      const y = pad.top + plotH - (v / maxSales) * plotH;
      return `${x},${y}`;
    });
    return `M${top.join(' L')} L${bottom.join(' L')} Z`;
  }

  const promoVals = data.map((d) => Math.abs(d.promotions));
  const ppcVals = data.map((d) => d.ppcSales);
  const organicVals = data.map((d) => d.organicSales);
  const base0 = data.map(() => 0);
  const base1 = promoVals;
  const base2 = promoVals.map((v, i) => v + ppcVals[i]);

  return (
    <div className="p-6 max-w-full">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-bold">Sales Trend</h1>
        <span className="text-xs text-gray-400">All amounts in USD</span>
      </div>

      {/* Controls */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="flex gap-1">
          {(['weekly', 'monthly'] as const).map((v) => (
            <button key={v} onClick={() => setView(v)}
              className={`px-3 py-1 text-sm rounded ${view === v ? 'bg-blue-600 text-white' : 'bg-gray-100 hover:bg-gray-200'}`}>
              {v === 'weekly' ? 'Weekly' : 'Monthly'}
            </button>
          ))}
        </div>
        <div className="flex gap-1">
          {[30, 60, 90, 180, 365].map((d) => (
            <button key={d} onClick={() => setDays(d)}
              className={`px-3 py-1 text-sm rounded ${days === d ? 'bg-blue-600 text-white' : 'bg-gray-100 hover:bg-gray-200'}`}>
              {d}d
            </button>
          ))}
        </div>

        {/* Marketplace buttons */}
        <div className="flex gap-1.5">
          <button onClick={() => setMarketplace('')}
            className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
              !marketplace ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50'
            }`}>All</button>
          {availableMarkets.map(id => (
            <button key={id} onClick={() => setMarketplace(id === marketplace ? '' : id)}
              className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
                marketplace === id ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50'
              }`}>{MARKETPLACE_LABELS[id] ?? id}</button>
          ))}
        </div>

        {/* Product group */}
        <select value={selectedGroup} onChange={e => { setSelectedGroup(e.target.value); setSelectedAsin(''); }}
          className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm">
          <option value="">All Products</option>
          {productGroups.map(g => (
            <option key={g.label} value={g.label}>{g.label} ({g.products.length})</option>
          ))}
        </select>

        {/* Variation (child ASIN) */}
        {activeGroup && activeGroup.products.length > 1 && (
          <select value={selectedAsin} onChange={e => setSelectedAsin(e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm">
            <option value="">All Variations</option>
            {activeGroup.products.map(p => {
              const variant = p.title.match(/\(([^)]+)\)$/)?.[1] ?? p.asin;
              return <option key={p.asin} value={p.asin}>{variant}</option>;
            })}
          </select>
        )}

        <ColumnConfig config={colConfig} onChange={setColConfig} />

        <label className="flex items-center gap-1 text-sm text-gray-600 ml-auto">
          <input type="checkbox" checked={showChart} onChange={(e) => setShowChart(e.target.checked)} />
          Show chart
        </label>
      </div>

      {loading && <p className="text-gray-400 text-sm">Loading...</p>}

      {/* Stacked Area Chart */}
      {showChart && data.length > 0 && (
        <div className="mb-6 overflow-x-auto border rounded-lg bg-white p-4">
          <div className="flex items-center gap-4 text-xs text-gray-500 mb-2">
            <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded" style={{background:'#3b82f6'}} /> Organic</span>
            <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded" style={{background:'#f59e0b'}} /> PPC</span>
            <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded" style={{background:'#a855f7'}} /> Promotions</span>
          </div>
          <svg width={chartW} height={chartH} className="block">
            {[0, 0.25, 0.5, 0.75, 1].map((p) => {
              const y = pad.top + plotH * (1 - p);
              return <g key={p}>
                <line x1={pad.left} y1={y} x2={chartW - pad.right} y2={y} stroke="#e5e7eb" strokeWidth={1} />
                <text x={pad.left - 4} y={y + 3} textAnchor="end" fontSize={9} fill="#9ca3af">
                  {fmtUsd(maxSales * p)}
                </text>
              </g>;
            })}
            <path d={buildArea(promoVals, base0)} fill="#a855f7" opacity={0.4} />
            <path d={buildArea(ppcVals, base1)} fill="#f59e0b" opacity={0.5} />
            <path d={buildArea(organicVals, base2)} fill="#3b82f6" opacity={0.5} />
            {data.map((d, i) => {
              const x = pad.left + i * (plotW / Math.max(data.length - 1, 1));
              const showLabel = data.length <= 15 || i % Math.ceil(data.length / 12) === 0;
              return showLabel ? (
                <text key={i} x={x} y={chartH - 4} textAnchor="middle" fontSize={9} fill="#6b7280">
                  {d.period.replace(/^\d{4}-/, '')}
                </text>
              ) : null;
            })}
          </svg>
        </div>
      )}

      {/* Event Tags */}
      <div className="mb-4 flex items-center gap-2 flex-wrap">
        {tags.map((t) => (
          <span key={t.id} className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full border"
            style={{ borderColor: t.color, color: t.color }}>
            <span className="inline-block w-2 h-2 rounded-full" style={{ background: t.color }} />
            {t.name} ({t.date})
            <button onClick={() => deleteTag(t.id)} className="ml-1 text-gray-400 hover:text-red-500">&times;</button>
          </span>
        ))}
        {!showTagForm ? (
          <button onClick={() => setShowTagForm(true)} className="text-xs text-blue-600 hover:text-blue-800">+ Add event tag</button>
        ) : (
          <div className="flex items-center gap-2 text-xs">
            <input placeholder="Event name" value={newTag.name} onChange={(e) => setNewTag({ ...newTag, name: e.target.value })}
              className="px-2 py-1 border rounded w-32" />
            <input type="date" value={newTag.date} onChange={(e) => setNewTag({ ...newTag, date: e.target.value })}
              className="px-2 py-1 border rounded" />
            <input type="color" value={newTag.color} onChange={(e) => setNewTag({ ...newTag, color: e.target.value })}
              className="w-6 h-6 border-0 p-0" />
            <button onClick={addTag} className="px-2 py-1 bg-blue-600 text-white rounded">Save</button>
            <button onClick={() => setShowTagForm(false)} className="text-gray-400">Cancel</button>
          </div>
        )}
      </div>

      {/* Trend Table */}
      {data.length > 0 && (
        <div className="overflow-x-auto border rounded-lg">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-50 border-b">
                <th className="text-left px-3 py-2 sticky left-0 bg-gray-50 z-10 min-w-[140px]">Metric</th>
                {data.map((d) => (
                  <th key={d.period} className="text-right px-3 py-2 min-w-[90px] whitespace-nowrap">
                    <div>{d.period.replace(/^\d{4}-/, '')}</div>
                    {d.dateRange && (
                      <div className="text-[10px] font-normal text-gray-400">{d.dateRange}</div>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibleMetrics.map((row) => (
                <tr key={row.key} className="border-b hover:bg-gray-50/50">
                  <td className="px-3 py-1.5 font-medium text-gray-700 sticky left-0 bg-white z-10">{row.label}</td>
                  {data.map((d) => {
                    const val = d[row.key] as number;
                    const bg = row.colorFn ? row.colorFn(val) : '';
                    return (
                      <td key={d.period} className={`px-3 py-1.5 text-right tabular-nums ${bg}`}>
                        {row.format(val)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && data.length === 0 && (
        <p className="text-gray-400 text-sm mt-4">No data for selected period.</p>
      )}
    </div>
  );
}
